/**
 * `MonitoringController` — Prompt.md §24, §22, §21 (quyết định #17).
 *
 * **Composition root duy nhất**: đây là nơi duy nhất `PostureMonitor`, `AlertEngine`,
 * `SessionManager` và `SessionRepository` được nối với nhau. Không component React nào import
 * trực tiếp từ `packages/*-core` — chúng chỉ thấy `UiSnapshot`.
 *
 * Hai điều đáng chú ý nhất trong file này:
 *
 * 1. **Hai trục thời gian được hợp nhất tại đây.** `VisionResult.timestamp` là media time của
 *    `<video>` (`currentTime * 1000`) — nó bắt đầu gần 0 và không so sánh được với `Date.now()`.
 *    `PostureMonitor` phải chạy trên trục vision (smoothing/state machine đo bằng chính timestamp
 *    của frame), còn `SessionManager` và `AlertEngine` chạy trên trục wall clock (`clock()`) vì
 *    `StudySession.startTime` phải là giờ thật để History hiển thị được. Trộn hai trục là bug âm
 *    thầm nhất có thể xảy ra ở đây: `elapsed` sẽ bị kẹp về 0 suốt phiên và Report ra 0 phút. Vì
 *    vậy snapshot đi vào session luôn được **đóng lại dấu thời gian** bằng wall clock.
 * 2. **Throttle nằm ở đây, không ở React** (§31): vision chạy 8–15 FPS, UI cập nhật 2–5 FPS.
 *    `getSnapshot()` trả về **cùng một object** giữa hai lần publish để `useSyncExternalStore`
 *    không re-render vô ích.
 */

import type { AppConfig, SessionConfig } from "@studystraight/shared-config";
import { statusLabels } from "@studystraight/shared-config";
import type {
  MonitorSnapshot,
  PostureBaseline,
  PostureEvent,
  PostureIssueType,
  PostureState,
  PostureStatus,
  SessionOptions,
  StudySession,
  TrackingState,
  VisionResult,
} from "@studystraight/shared-types";
import { AlertEngine, type AlertProvider } from "@studystraight/alert-core";
import { PostureMonitor, resolveExcludedIssues } from "@studystraight/posture-core";
import { SessionManager, type SessionRepository } from "@studystraight/session-core";

import { formatClock } from "../../app/format.js";

/** §24 — đúng những gì màn hình Monitoring vẽ, không hơn. Metrics kỹ thuật chỉ đi vào Debug Mode. */
export interface UiSnapshot {
  running: boolean;
  paused: boolean;
  /** 0..100 (§17). Chỉ có nghĩa khi `trackingLost === false`. */
  score: number;
  status: PostureStatus;
  statusLabel: string;
  elapsedLabel: string;
  /** `null` khi phiên không đặt mục tiêu (§23). */
  remainingLabel: string | null;
  alertCount: number;
  trackingLost: boolean;
  /** Câu nhắc gần nhất — băng chữ §20, cũng là fallback khi thiết bị không có giọng Việt. */
  lastMessage: string | null;
  /** §23 — nhắc nghỉ mắt, hiển thị tới khi người dùng bấm bỏ qua. */
  eyeBreakDue: boolean;
  goalReached: boolean;
  /** Ghi storage thất bại (hết quota, private mode) — §27: phải nói, không được im lặng. */
  storageFailed: boolean;
}

export interface MonitoringControllerDeps {
  monitor: PostureMonitor;
  alerts: AlertEngine;
  session: SessionManager;
  repo: SessionRepository;
  /** Chỗ **duy nhất** trong toàn hệ thống đọc giờ hệ thống (quyết định #8). */
  clock: () => number;
  sessionConfig: SessionConfig;
  /** §31 — 2–5 FPS. Mặc định 250ms (4 FPS). */
  publishIntervalMs?: number;
}

const IDLE_SNAPSHOT: UiSnapshot = {
  running: false,
  paused: false,
  score: 100,
  status: "GOOD",
  statusLabel: statusLabels.GOOD,
  elapsedLabel: formatClock(0),
  remainingLabel: null,
  alertCount: 0,
  trackingLost: true,
  lastMessage: null,
  eyeBreakDue: false,
  goalReached: false,
  storageFailed: false,
};

export class MonitoringController {
  private readonly listeners = new Set<() => void>();
  private published: UiSnapshot = IDLE_SNAPSHOT;
  private lastPublishAt = 0;

  /** Neo để đổi từ trục vision sang wall clock — xem ghi chú (1) ở đầu file. */
  private frameBase: { vision: number; wall: number } | null = null;

  private status: PostureStatus = "GOOD";
  private state: PostureState = "GOOD";
  private score = 100;
  private tracking: TrackingState = "LOST";
  private elapsedMs = 0;
  private remainingMs: number | null = null;
  private lastMessage: string | null = null;
  private eyeBreakDue = false;
  private goalReached = false;
  private storageFailed = false;
  /** TTS đang nói: không xếp hàng thêm câu nữa (§21 — một câu mỗi lần). */
  private speaking = false;

  constructor(private readonly deps: MonitoringControllerDeps) {}

  get baseline(): PostureBaseline {
    return this.deps.monitor.baseline;
  }

  get excludedIssues(): readonly PostureIssueType[] {
    return resolveExcludedIssues(this.deps.monitor.baseline);
  }

  /** Trạng thái thô cho Debug Mode (§25) — UI người dùng không được đọc cái này. */
  get debugState(): { state: PostureState; tracking: TrackingState } {
    return { state: this.state, tracking: this.tracking };
  }

  /**
   * Bắt đầu phiên.
   *
   * Ghi snapshot **ngay** thay vì chờ mốc 15 giây đầu tiên: nếu tab bị kill trong 15 giây đầu,
   * `getPendingSnapshot()` không có gì để phục hồi và ta không phân biệt được "chưa từng học" với
   * "vừa mất phiên".
   */
  async startSession(options: SessionOptions): Promise<void> {
    const excludedIssues = resolveExcludedIssues(this.deps.monitor.baseline);

    this.deps.monitor.reset();
    this.deps.alerts.reset();
    this.deps.alerts.setUnavailableIssues(excludedIssues);

    this.frameBase = null;
    this.status = "GOOD";
    this.state = "GOOD";
    this.score = 100;
    this.tracking = "LOST";
    this.elapsedMs = 0;
    this.remainingMs = null;
    this.lastMessage = null;
    this.eyeBreakDue = false;
    this.goalReached = false;
    this.storageFailed = false;

    const now = this.deps.clock();
    this.deps.session.start({ ...options, excludedIssues }, now);
    this.publish(true);
    await this.saveSnapshot(now);
  }

  /** Gọi ở **mọi** frame vision (không throttle) — throttle chỉ áp cho việc publish ra UI. */
  pushFrame(vision: VisionResult): void {
    if (!this.deps.session.isRunning) return;

    const wall = this.deps.clock();
    this.frameBase = { vision: vision.timestamp, wall };
    this.consume(this.deps.monitor.push(vision), wall);
  }

  /**
   * Nhịp "không có frame".
   *
   * Khi trẻ rời khỏi bàn, pose engine không thấy người nên provider trả `null` và `pushFrame()`
   * **không hề được gọi**. Không có nhịp này, UI đứng mãi ở "TỐT" của frame cuối cùng.
   */
  tick(): void {
    if (!this.deps.session.isRunning) return;

    const wall = this.deps.clock();
    this.consume(this.deps.monitor.tick(this.visionNow(wall)), wall);
  }

  pause(): void {
    if (!this.deps.session.isRunning) return;
    this.deps.session.pause(this.deps.clock());
    this.publish(true);
  }

  /**
   * Tiếp tục sau khi tạm dừng.
   *
   * `monitor.reset()` là bắt buộc chứ không phải cho gọn: sau 5 phút pause, đồng hồ lỗi và cửa sổ
   * smoothing vẫn giữ frame trước lúc dừng — không xoá thì trẻ vừa ngồi lại đã bị nhắc
   * "con cúi đầu 5 phút rồi".
   */
  resume(): void {
    if (this.deps.session.state !== "PAUSED") return;
    this.deps.monitor.reset();
    this.frameBase = null;
    this.tracking = "LOST";
    this.deps.session.resume(this.deps.clock());
    this.publish(true);
  }

  /** Người dùng đã đọc nhắc nghỉ mắt. */
  dismissEyeBreak(): void {
    if (!this.eyeBreakDue) return;
    this.eyeBreakDue = false;
    this.publish(true);
  }

  /** §20 — tắt/bật giọng nói giữa phiên. */
  setMuted(muted: boolean): void {
    this.deps.alerts.setMuted(muted);
  }

  /**
   * Kết thúc phiên và lưu.
   *
   * Phiên quá ngắn (§26 `minReportableDurationMs`) **không** được lưu vào History — nhưng vẫn
   * được trả về để Report nói "phiên quá ngắn để thống kê" thay vì hiện một bảng số 0.
   */
  async endSession(): Promise<StudySession> {
    const session = this.deps.session.end(this.deps.clock());
    this.publish(true);

    if (session.duration >= this.deps.sessionConfig.minReportableDurationMs) {
      await this.deps.repo.save(session);
    } else {
      await this.deps.repo.clearSnapshot();
    }
    return session;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getSnapshot(): UiSnapshot {
    return this.published;
  }

  private consume(snapshot: MonitorSnapshot, wall: number): void {
    this.status = snapshot.result.status;
    this.state = snapshot.state;
    this.score = snapshot.result.postureScore;
    this.tracking = snapshot.tracking;

    // Đóng lại dấu thời gian bằng wall clock trước khi rời khỏi PostureMonitor — ghi chú (1).
    const push = this.deps.session.push({ ...snapshot, timestamp: wall });
    this.elapsedMs = push.elapsedMs;
    this.remainingMs = push.remainingMs;

    for (const event of push.timerEvents) {
      if (event === "EYE_BREAK") {
        this.eyeBreakDue = true;
        this.speak(wall);
      } else {
        this.goalReached = true;
      }
    }

    this.dispatchAlerts(snapshot.events, wall);
    if (push.snapshotDue) void this.saveSnapshot(wall);

    this.publish(push.timerEvents.length > 0);
  }

  /**
   * `now` trên trục vision, suy ra từ frame gần nhất cộng thời gian thực đã trôi.
   *
   * Không dùng `clock()` trực tiếp: hai trục lệch nhau một hằng số bất kỳ (media time bắt đầu từ
   * 0), nên đưa wall clock vào `monitor.tick()` sẽ khiến `trackingAt()` thấy khoảng cách hàng giờ
   * và báo mất tracking ngay frame đầu.
   */
  private visionNow(wall: number): number {
    const base = this.frameBase;
    return base === null ? 0 : base.vision + (wall - base.wall);
  }

  /**
   * `AlertEngine.handle()` là async (TTS). Bỏ event khi đang nói thay vì xếp hàng: hàng đợi ở
   * 12 FPS sẽ phát ra một tràng câu nhắc sau khi trẻ đã ngồi thẳng lại.
   */
  private dispatchAlerts(events: readonly PostureEvent[], wall: number): void {
    if (events.length === 0 || this.speaking) return;

    this.speaking = true;
    void this.deps.alerts
      .handle(events, wall)
      .then((decision) => {
        if (!decision.emit) return;
        this.deps.session.recordAlert();
        this.lastMessage = decision.message ?? null;
      })
      .finally(() => {
        this.speaking = false;
        this.publish(true);
      });
  }

  private speak(wall: number): void {
    if (this.speaking) return;
    this.speaking = true;
    void this.deps.alerts.speakMessage("EYE_BREAK", wall).finally(() => {
      this.speaking = false;
    });
  }

  private async saveSnapshot(wall: number): Promise<void> {
    await this.deps.repo.saveSnapshot(this.deps.session.toSnapshot(wall));
  }

  private publish(force: boolean): void {
    const now = this.deps.clock();
    const interval = this.deps.publishIntervalMs ?? 250;
    if (!force && now - this.lastPublishAt < interval) return;

    this.lastPublishAt = now;
    this.published = {
      running: this.deps.session.state === "RUNNING",
      paused: this.deps.session.state === "PAUSED",
      score: this.score,
      status: this.status,
      statusLabel: statusLabels[this.status],
      elapsedLabel: formatClock(this.elapsedMs),
      remainingLabel: this.remainingMs === null ? null : formatClock(this.remainingMs),
      alertCount: this.deps.alerts.alertCount,
      trackingLost: this.tracking === "LOST",
      lastMessage: this.lastMessage,
      eyeBreakDue: this.eyeBreakDue,
      goalReached: this.goalReached,
      storageFailed: this.storageFailed,
    };
    for (const listener of this.listeners) listener();
  }

  /** Gọi từ adapter storage khi một thao tác ghi thất bại (§27). */
  reportStorageFailure(): void {
    if (this.storageFailed) return;
    this.storageFailed = true;
    this.publish(true);
  }
}

export interface CreateMonitoringControllerOptions {
  baseline: PostureBaseline;
  /** Đã resolve theo `baseline.placement` + override của Settings > Developer (§14). */
  config: AppConfig;
  repository: SessionRepository;
  provider?: AlertProvider;
  /** §20 — người dùng tắt giọng nói trong Settings. */
  muted?: boolean;
  clock?: () => number;
}

/**
 * Dựng toàn bộ engine cho một phiên.
 *
 * Đây là hàm duy nhất trong `apps/web` gọi `new PostureMonitor` / `new AlertEngine` /
 * `new SessionManager`. React chỉ gọi hàm này rồi làm việc với `UiSnapshot`.
 */
export function createMonitoringController(
  options: CreateMonitoringControllerOptions,
): MonitoringController {
  const alerts = new AlertEngine(options.config.alert, options.provider);
  alerts.setMuted(options.muted ?? false);

  return new MonitoringController({
    monitor: new PostureMonitor({ baseline: options.baseline, config: options.config }),
    alerts,
    session: new SessionManager({ config: options.config.session }),
    repo: options.repository,
    // `Date.now()` chỉ xuất hiện ở đây trong toàn bộ pipeline tư thế (quyết định #8).
    clock: options.clock ?? ((): number => Date.now()),
    sessionConfig: options.config.session,
    publishIntervalMs: Math.round(1000 / options.config.vision.uiUpdateFps),
  });
}
